import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronUp, ChevronDown, Eye, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export default function DriverRolodex({ drivers, onDelete, currentUser }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('down');
  const navigate = useNavigate();

  // Sort drivers by surname (last name)
  const getSurname = (fullName) => {
    const parts = fullName.trim().split(' ');
    return parts[parts.length - 1].toUpperCase();
  };

  const sortedDrivers = [...drivers].sort((a, b) => {
    const surnameA = getSurname(a.name);
    const surnameB = getSurname(b.name);
    return surnameA.localeCompare(surnameB);
  });

  // Get alphabet tabs with driver counts
  const getAlphabetTabs = () => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const tabs = [];
    
    alphabet.forEach(letter => {
      const driversWithLetter = sortedDrivers.filter(d => getSurname(d.name).startsWith(letter));
      if (driversWithLetter.length > 0) {
        const firstIndex = sortedDrivers.findIndex(d => getSurname(d.name).startsWith(letter));
        tabs.push({ letter, count: driversWithLetter.length, index: firstIndex });
      }
    });
    
    return tabs;
  };

  const alphabetTabs = getAlphabetTabs();
  
  // Get current active letter
  const currentDriver = sortedDrivers[currentIndex];
  const currentLetter = currentDriver ? getSurname(currentDriver.name)[0] : '';

  const handleNext = () => {
    setDirection('down');
    setCurrentIndex((prev) => (prev + 1) % sortedDrivers.length);
  };

  const handlePrev = () => {
    setDirection('up');
    setCurrentIndex((prev) => (prev - 1 + sortedDrivers.length) % sortedDrivers.length);
  };

  const handleLetterClick = (index) => {
    if (index > currentIndex) {
      setDirection('down');
    } else {
      setDirection('up');
    }
    setCurrentIndex(index);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowDown') handleNext();
    if (e.key === 'ArrowUp') handlePrev();
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY > 0) {
      // Scrolling down - go to next card
      handleNext();
    } else if (e.deltaY < 0) {
      // Scrolling up - go to previous card
      handlePrev();
    }
  };

  if (drivers.length === 0) {
    return (
      <div className="text-center py-12" data-testid="no-drivers-message">
        <p className="text-slate-500">No drivers found. Add your first driver to get started.</p>
      </div>
    );
  }

  const nextDriver = sortedDrivers[(currentIndex + 1) % sortedDrivers.length];
  const prevDriver = sortedDrivers[(currentIndex - 1 + sortedDrivers.length) % sortedDrivers.length];

  return (
    <div className="space-y-4">
      {/* Alphabet Tabs */}
      <div className="flex flex-wrap gap-1 justify-center no-print" data-testid="alphabet-tabs">
        {alphabetTabs.map(({ letter, count, index }) => (
          <Button
            key={letter}
            variant={currentLetter === letter ? 'default' : 'outline'}
            size="sm"
            className={`w-10 h-10 p-0 ${currentLetter === letter ? 'bg-blue-600' : ''}`}
            onClick={() => handleLetterClick(index)}
            data-testid={`letter-tab-${letter}`}
            title={`${count} driver${count > 1 ? 's' : ''}`}
          >
            {letter}
          </Button>
        ))}
      </div>

      <div 
      className="relative h-[600px] flex flex-col items-center justify-center"
      onKeyDown={handleKeyPress}
      onWheel={handleWheel}
      tabIndex={0}
      data-testid="driver-rolodex"
    >
      {/* Navigation Buttons */}
      <Button
        variant="outline"
        size="icon"
        className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20 rounded-full h-12 w-12 shadow-lg hover:scale-110 transition-transform"
        onClick={handleNext}
        disabled={drivers.length <= 1}
        data-testid="prev-driver-button"
      >
        <ChevronUp className="h-6 w-6" />
      </Button>

      <Button
        variant="outline"
        size="icon"
        className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20 rounded-full h-12 w-12 shadow-lg hover:scale-110 transition-transform"
        onClick={handlePrev}
        disabled={drivers.length <= 1}
        data-testid="next-driver-button"
      >
        <ChevronDown className="h-6 w-6" />
      </Button>

      {/* Card Stack - Vertical */}
      <div className="relative w-full max-w-lg h-full flex flex-col items-center justify-center">
        {/* Previous Card (behind, top) */}
        <div
          className="absolute w-full transition-all duration-500 ease-out"
          style={{
            transform: 'translateY(-40%) scale(0.85)',
            opacity: 0.5,
            zIndex: 1,
            top: '10%'
          }}
        >
          <DriverCard driver={prevDriver} simple />
        </div>

        {/* Current Card (front, center) - overlaps top and bottom */}
        <div
          key={currentDriver.id}
          className={`absolute w-full transition-all duration-500 ease-out ${
            direction === 'down' ? 'animate-slide-in-down' : 'animate-slide-in-up'
          }`}
          style={{
            transform: 'translateY(0) scale(1)',
            zIndex: 10,
            top: '50%',
            marginTop: '-150px'
          }}
        >
          <DriverCard 
            driver={currentDriver} 
            onDelete={onDelete}
            onView={() => navigate(`/drivers/${currentDriver.id}`)}
            currentUser={currentUser}
            featured
          />
        </div>

        {/* Next Card (behind, bottom) */}
        <div
          className="absolute w-full transition-all duration-500 ease-out"
          style={{
            transform: 'translateY(40%) scale(0.85)',
            opacity: 0.5,
            zIndex: 1,
            bottom: '10%'
          }}
        >
          <DriverCard driver={nextDriver} simple />
        </div>
      </div>

      {/* Card Counter */}
      <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-md border border-slate-200 z-30">
        <p className="text-sm font-medium text-slate-700">
          {currentIndex + 1} / {sortedDrivers.length}
        </p>
      </div>
    </div>
    </div>
  );
}

function DriverCard({ driver, onDelete, onView, currentUser, featured, simple }) {
  if (simple) {
    return (
      <Card className="transition-shadow bg-white/80 dark:bg-slate-800/80 backdrop-blur">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="text-base dark:text-slate-100">{driver.name}</span>
            <Badge variant={driver.status === 'active' ? 'default' : 'secondary'} className="text-xs">
              {driver.status}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {driver.employee_id || 'No ID'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="shadow-2xl transition-all duration-300 border-2 border-blue-200 dark:border-blue-900"
      data-testid={`driver-card-${driver.id}`}
    >
      <CardHeader className="bg-gradient-to-br from-blue-50 via-white to-slate-50 dark:from-blue-950 dark:via-slate-800 dark:to-slate-900 border-b-2 border-blue-100 dark:border-blue-900">
        <CardTitle className="flex items-center justify-between">
          <div>
            <h3 className="text-3xl font-bold text-slate-800 dark:text-slate-100">{driver.name}</h3>
            {driver.employee_id && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">Employee ID: {driver.employee_id}</p>
            )}
          </div>
          <Badge 
            variant={driver.status === 'active' ? 'default' : 'secondary'}
            className="text-base px-4 py-2"
          >
            {driver.status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {driver.hire_date && (
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Hire Date</span>
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {format(new Date(driver.hire_date), 'MMMM d, yyyy')}
            </span>
          </div>
        )}
        
        <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Employment Status</span>
          <span className="text-sm font-semibold text-blue-800 dark:text-blue-200 capitalize">
            {driver.status}
          </span>
        </div>

        <div className="flex space-x-2 pt-4">
          <Button
            variant="default"
            className="flex-1 h-12 text-base"
            onClick={onView}
            data-testid={`view-driver-${driver.id}`}
          >
            <Eye className="h-5 w-5 mr-2" />
            View Full Details
          </Button>
          {currentUser?.role === 'admin' && (
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12"
              onClick={() => onDelete(driver.id)}
              data-testid={`delete-driver-${driver.id}`}
            >
              <Trash2 className="h-5 w-5" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
